import { Capacitor } from '@capacitor/core';
import { AdMob, BannerAdPosition, BannerAdSize } from '@capacitor-community/admob';

// Google Official Test Ad Unit IDs for Android
export const GOOGLE_TEST_BANNER_ID = 'ca-app-pub-3940256099942544/6300978111';
export const GOOGLE_TEST_INTERSTITIAL_ID = 'ca-app-pub-3940256099942544/1033173712';

// Active Ad Unit IDs
export const ADMOB_BANNER_ID =
  process.env.REACT_APP_ADMOB_BANNER_ID || GOOGLE_TEST_BANNER_ID;
export const ADMOB_INTERSTITIAL_ID =
  process.env.REACT_APP_ADMOB_INTERSTITIAL_ID || GOOGLE_TEST_INTERSTITIAL_ID;

let isInitialized = false;
let isBannerVisible = false;
let interstitialPrepared = false;
let lastInterstitialTime = 0;

export async function initializeAdMob() {
  if (!Capacitor.isNativePlatform() || isInitialized) return;

  try {
    await AdMob.initialize({
      requestTrackingAuthorization: true,
      testingDevices: ['EMULATOR'],
      initializeForTesting: process.env.NODE_ENV !== 'production' || ADMOB_BANNER_ID === GOOGLE_TEST_BANNER_ID,
    });
    isInitialized = true;
  } catch (err) {
    console.warn('AdMob initialization notice:', err?.message || err);
  }
}

/**
 * Show a responsive AdMob banner at the TOP of the screen.
 * This ensures it is 100% visible and NEVER floats over or blocks the bottom navigation menu.
 */
export async function showTopBannerAd(customAdId) {
  if (!Capacitor.isNativePlatform()) return;

  try {
    await initializeAdMob();

    const adId = customAdId || ADMOB_BANNER_ID;

    if (isBannerVisible) {
      try {
        await AdMob.removeBanner();
      } catch (_) {}
      isBannerVisible = false;
    }

    try {
      await AdMob.showBanner({
        adId,
        adSize: BannerAdSize.ADAPTIVE_BANNER,
        position: BannerAdPosition.TOP_CENTER,
        margin: 0,
        isTesting: adId === GOOGLE_TEST_BANNER_ID,
      });
      isBannerVisible = true;
    } catch (bannerErr) {
      // Graceful fallback to Google test banner if custom ad unit is still propagating
      console.warn('Fallback to test banner unit:', bannerErr?.message || bannerErr);
      await AdMob.showBanner({
        adId: GOOGLE_TEST_BANNER_ID,
        adSize: BannerAdSize.ADAPTIVE_BANNER,
        position: BannerAdPosition.TOP_CENTER,
        margin: 0,
        isTesting: true,
      });
      isBannerVisible = true;
    }
  } catch (err) {
    console.warn('AdMob show banner notice:', err?.message || err);
  }
}

// Alias for backwards compatibility
export const showHomeBannerAd = showTopBannerAd;

/**
 * Remove any visible banner ad (used on auth pages or when unmounting).
 */
export async function hideHomeBannerAd() {
  if (!Capacitor.isNativePlatform()) return;
  try {
    await AdMob.hideBanner();
  } catch (_) {}
}

export async function resumeHomeBannerAd() {
  // We use clean in-page AdMobBanner components instead of floating overlays
  // to ensure the top navigation (toggle menu, cart) and bottom navigation never get covered or moved.
  return;
}

export async function removeHomeBannerAd() {
  if (!Capacitor.isNativePlatform()) return;

  try {
    await AdMob.removeBanner();
    isBannerVisible = false;
  } catch (_) {
    // Ignore if no banner was currently active
  }
}

/**
 * Pre-load a full-screen Interstitial Ad.
 */
export async function preloadInterstitialAd() {
  if (!Capacitor.isNativePlatform()) return;

  try {
    await initializeAdMob();
    await AdMob.prepareInterstitial({
      adId: ADMOB_INTERSTITIAL_ID,
      isTesting: ADMOB_INTERSTITIAL_ID === GOOGLE_TEST_INTERSTITIAL_ID,
    });
    interstitialPrepared = true;
  } catch (err) {
    // Retry with test ID
    try {
      await AdMob.prepareInterstitial({
        adId: GOOGLE_TEST_INTERSTITIAL_ID,
        isTesting: true,
      });
      interstitialPrepared = true;
    } catch (_) {
      interstitialPrepared = false;
    }
  }
}

/**
 * Show a full-screen Interstitial Ad (with smart throttle of min 30s between ads).
 */
export async function showInterstitialAd() {
  if (!Capacitor.isNativePlatform()) return;

  const now = Date.now();
  if (now - lastInterstitialTime < 25000) {
    return; // Don't overwhelm user with frequent full-screen ads
  }

  try {
    await initializeAdMob();

    if (!interstitialPrepared) {
      await preloadInterstitialAd();
    }

    await AdMob.showInterstitial();
    lastInterstitialTime = Date.now();
    interstitialPrepared = false;

    // Immediately preload next one in the background
    window.setTimeout(() => {
      preloadInterstitialAd();
    }, 4000);
  } catch (err) {
    console.warn('AdMob show interstitial notice:', err?.message || err);
    interstitialPrepared = false;
  }
}
